import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUpIcon, ShieldCheckIcon, BarChart3Icon } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { GlobalStatsSkeleton } from '../components/SkeletonLoader';
import { useCryptoData } from '../hooks/useCryptoData';
import { useExchangeData } from '../hooks/useExchangeData';
import { useGlobalMetrics } from '../hooks/useGlobalMetrics';
export function HomePage() {
  const {
    data: cryptocurrencies
  } = useCryptoData(5);
  const {
    data: exchanges
  } = useExchangeData();
  const {
    metrics,
    loading: metricsLoading
  } = useGlobalMetrics();
  const topCryptos = cryptocurrencies.slice(0, 5);
  const topExchanges = exchanges.slice(0, 5);
  const formatLargeNumber = (num: number) => {
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };
  return <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-id="element-581">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20" data-id="element-582">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-583">
          <div className="text-center max-w-3xl mx-auto" data-id="element-584">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-2 mb-6" data-id="element-585">
              <ShieldCheckIcon size={18} data-id="element-586" />
              <span className="text-sm font-medium" data-id="element-587">
                Trusted Crypto Intelligence
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-588">
              The TrustPilot for{' '}
              <span className="text-blue-400" data-id="element-589">Cryptocurrency</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-590">
              Comprehensive reviews, trust scores, and intelligence for
              cryptocurrencies and exchanges. Make informed decisions with
              verified data.
            </p>

            {/* Global Market Stats */}
            {metricsLoading ? <GlobalStatsSkeleton data-id="element-591" /> : metrics ? <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-592">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" data-id="element-593">
                  <div className="text-sm text-slate-300 mb-1" data-id="element-594">
                    Total Market Cap
                  </div>
                  <div className="text-2xl font-bold" data-id="element-595">
                    {formatLargeNumber(metrics.totalMarketCap)}
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" data-id="element-596">
                  <div className="text-sm text-slate-300 mb-1" data-id="element-597">24h Volume</div>
                  <div className="text-2xl font-bold" data-id="element-598">
                    {formatLargeNumber(metrics.total24hVolume)}
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" data-id="element-599">
                  <div className="text-sm text-slate-300 mb-1" data-id="element-600">
                    BTC Dominance
                  </div>
                  <div className="text-2xl font-bold" data-id="element-601">
                    {metrics.btcDominance.toFixed(1)}%
                  </div>
                </div>
              </div> : null}

            <div className="flex flex-wrap gap-4 justify-center" data-id="element-602">
              <Link to="/cryptocurrencies" data-id="element-603">
                <Button size="lg" className="shadow-lg shadow-blue-500/20" data-id="element-604">
                  Explore Cryptocurrencies
                </Button>
              </Link>
              <Link to="/exchanges" data-id="element-605">
                <Button variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-id="element-606">
                  View Exchanges
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="bg-yellow-50 border-y border-yellow-200" data-id="element-607">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-608">
          <div className="flex items-start gap-3" data-id="element-609">
            <div className="flex-shrink-0 mt-0.5" data-id="element-610">
              <div className="w-1 h-full bg-yellow-500 rounded-full" data-id="element-611"></div>
            </div>
            <div className="flex-1" data-id="element-612">
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-613">
                <strong className="font-semibold text-gray-900" data-id="element-614">
                  Disclaimer:
                </strong>{' '}
                This website aggregates crypto-related information from various
                public sources. The content is provided for informational
                purposes only and does not constitute financial or investment
                advice. Always conduct independent research before making
                financial decisions.{' '}
                <Link to="/terms-of-use" className="text-blue-600 hover:text-blue-700 font-semibold underline" data-id="element-615">
                  Read full Terms
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-616">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-617">
          <Card className="p-6 text-center" hover data-id="element-618">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-619">
              <ShieldCheckIcon className="text-blue-600" size={24} data-id="element-620" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-621">Trust Scores</h3>
            <p className="text-slate-600" data-id="element-622">
              Proprietary trust scoring system based on security, transparency,
              and user reviews.
            </p>
          </Card>

          <Card className="p-6 text-center" hover data-id="element-623">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-624">
              <BarChart3Icon className="text-green-600" size={24} data-id="element-625" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-626">Live Market Data</h3>
            <p className="text-slate-600" data-id="element-627">
              Real-time pricing, market cap, volume, and performance metrics
              from CoinMarketCap.
            </p>
          </Card>

          <Card className="p-6 text-center" hover data-id="element-628">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-629">
              <TrendingUpIcon className="text-purple-600" size={24} data-id="element-630" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-631">Verified Reviews</h3>
            <p className="text-slate-600" data-id="element-632">
              Authentic user reviews from verified traders and investors in the
              crypto space.
            </p>
          </Card>
        </div>
      </section>

      {/* Top Cryptocurrencies Preview */}
      <section className="py-16 bg-slate-50" data-id="element-633">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-634">
          <div className="flex items-center justify-between mb-8" data-id="element-635">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-636">
              Top Cryptocurrencies
            </h2>
            <Link to="/cryptocurrencies" data-id="element-637">
              <Button variant="outline" data-id="element-638">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4" data-id="element-639">
            {topCryptos.map(crypto => <Link key={crypto.id} to={`/cryptocurrency/${crypto.id}`} data-id="element-640">
                <Card className="p-5" hover data-id="element-641">
                  <div className="flex items-center justify-between" data-id="element-642">
                    <div className="flex items-center gap-4" data-id="element-643">
                      <div className="flex items-center gap-3" data-id="element-644">
                        <span className="text-slate-500 font-semibold text-sm w-6" data-id="element-645">
                          {crypto.rank}
                        </span>
                        <img src={crypto.logo} alt={crypto.name} className="w-10 h-10" data-id="element-646" />
                      </div>
                      <div data-id="element-647">
                        <div className="flex items-center gap-2 mb-1" data-id="element-648">
                          <h3 className="font-semibold text-lg" data-id="element-649">
                            {crypto.name}
                          </h3>
                          <span className="text-slate-500 text-sm" data-id="element-650">
                            {crypto.symbol}
                          </span>
                        </div>
                        <TrustScore score={crypto.trustScore} size="sm" data-id="element-651" />
                      </div>
                    </div>

                    <div className="text-right" data-id="element-652">
                      <div className="font-semibold text-lg mb-1" data-id="element-653">
                        $
                        {crypto.price >= 1 ? crypto.price.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }) : crypto.price.toFixed(6)}
                      </div>
                      <div className={`text-sm font-medium ${crypto.change24h >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-654">
                        {crypto.change24h >= 0 ? '+' : ''}
                        {crypto.change24h.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Top Exchanges Preview */}
      <section className="py-16" data-id="element-655">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-656">
          <div className="flex items-center justify-between mb-8" data-id="element-657">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-658">Top Exchanges</h2>
            <Link to="/exchanges" data-id="element-659">
              <Button variant="outline" data-id="element-660">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-661">
            {topExchanges.map(exchange => <Link key={exchange.id} to={`/exchange/${exchange.id}`} data-id="element-662">
                <Card className="p-5" hover data-id="element-663">
                  <div className="flex items-center gap-3 mb-4" data-id="element-664">
                    <div className="flex items-center gap-2" data-id="element-665">
                      <span className="text-slate-500 font-semibold text-sm w-6" data-id="element-666">
                        {exchange.rank}
                      </span>
                      <img src={exchange.logo} alt={exchange.name} className="w-10 h-10" data-id="element-667" />
                    </div>
                    <div data-id="element-668">
                      <h3 className="font-semibold text-lg" data-id="element-669">{exchange.name}</h3>
                      <TrustScore score={exchange.trustScore} size="sm" data-id="element-670" />
                    </div>
                  </div>

                  <div className="space-y-2 text-sm" data-id="element-671">
                    <div className="flex justify-between" data-id="element-672">
                      <span className="text-slate-600" data-id="element-673">24h Volume:</span>
                      <span className="font-medium" data-id="element-674">
                        ${(exchange.tradingVolume / 1000000000).toFixed(1)}B
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-675">
                      <span className="text-slate-600" data-id="element-676">Coins:</span>
                      <span className="font-medium" data-id="element-677">
                        {exchange.supportedCoins}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-678">
                      <span className="text-slate-600" data-id="element-679">Trading Fee:</span>
                      <span className="font-medium" data-id="element-680">{exchange.tradingFee}</span>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white" data-id="element-681">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-682">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-683">
            Ready to Make Informed Decisions?
          </h2>
          <p className="text-xl text-blue-100 mb-8" data-id="element-684">
            Join thousands of investors who trust CryptoTopReviews for their
            crypto intelligence.
          </p>
          <div className="flex flex-wrap gap-4 justify-center" data-id="element-685">
            <Link to="/cryptocurrencies" data-id="element-686">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50" data-id="element-687">
                Start Exploring
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>;
}